import { Zap, BadgeCheck, HeartHandshake, Building2 } from "lucide-react";

const SHOWCASE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/9ffe81b5-c474-4e56-ae56-bf9c315b9247/images/7c66eb371a5bce57311bc4ab16ba2c82cf3559faa96f2df965134d252e38bdca.png";

const PILLARS = [
  {
    icon: Zap,
    title: "Fast Turnaround",
    text: "Most services completed in 15–30 minutes — no DMV line, no all-day wait.",
  },
  {
    icon: BadgeCheck,
    title: "Done Right the First Time",
    text: "Modern systems + experienced staff = paperwork the DMV won't kick back.",
  },
  {
    icon: HeartHandshake,
    title: "Real Customer Care",
    text: "Friendly, bilingual team that explains every step before you sign.",
  },
  {
    icon: Building2,
    title: "Comprehensive Services",
    text: "From renewals to commercial fleet plates — one stop for everything DMV.",
  },
];

const STATS = [
  { value: "12+", label: "Years on El Cajon Blvd" },
  { value: "10K+", label: "Vehicles registered" },
  { value: "<30m", label: "Average visit time" },
  { value: "5.0★", label: "Avg. customer rating" },
];

export default function WhyUs() {
  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="relative py-24 sm:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left: image + stats */}
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-sm aspect-[4/5]">
              <img
                src={SHOWCASE_IMG}
                alt="Paperwork and car keys"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-7">
                <div className="text-xs uppercase tracking-[0.22em] text-white/80 font-semibold">
                  Why Virtue
                </div>
                <div className="mt-2 font-display text-white text-2xl sm:text-3xl font-semibold leading-tight">
                  We take the headache, you keep the day.
                </div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
              {STATS.map((s) => (
                <div
                  key={s.label}
                  className="bg-slate-50 border border-slate-200 rounded-xl p-5"
                >
                  <div className="font-display text-3xl font-bold text-slate-900 tracking-tight">
                    {s.value}
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: pillars */}
          <div className="lg:col-span-7">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-blue-600">
              About Us
            </div>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900 leading-[1.05]">
              More than a registration shop — your trusted partner in vehicle ownership.
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              At Virtue Registration Services we're more than just an auto registration center.
              With years of experience and a deep understanding of California DMV regulations,
              our team brings accuracy, efficiency, and professionalism to every transaction.
              We know how time-consuming and frustrating DMV trips can be — so we built a faster way.
            </p>

            <div className="mt-10 grid sm:grid-cols-2 gap-5">
              {PILLARS.map((p, idx) => {
                const I = p.icon;
                return (
                  <div
                    key={p.title}
                    data-testid={`pillar-${idx}`}
                    className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-blue-200 hover:shadow-md transition-all"
                  >
                    <div className="h-11 w-11 rounded-xl bg-slate-900 text-white grid place-items-center">
                      <I className="h-5 w-5" />
                    </div>
                    <h3 className="mt-5 font-display text-lg font-semibold text-slate-900">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                      {p.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
